import React, { useEffect, useMemo, useRef, useState } from 'react';
import './postDetailCard.scss';

import {
  HeartOutlined,
  MessageOutlined,
  ShopOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Carousel,
  DatePicker,
  Input,
  Modal,
  Select,
  Spin,
  Statistic,
  Tag,
  message,
} from 'antd';

import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import SkuType from '@/Interfaces/I_Sku';
import { UserRoleEnums } from '@/Interfaces/I_userRole';
import { VodTag } from '@/Interfaces/I_vod';

import { updatePostAnnouncement, updateVodPostTag } from '@/Redux/slices/ModalSlice';

import EditSkuModal, { PinnedSku, PostSku } from '@/Components/EditSkuModal';
import PostVideoPlayer from '@/Components/PostVideoPlayer';

import {
  convertTimeObjectToMiliSecond,
  getTimeObjectFromMiliSecond,
  TimeObject,
  timeTranslate,
} from '@/Utils/timeTranslate';

import moment, { Moment } from 'moment';
import { patchPostData } from '@/Redux/slices/PostManagementSlice';
import PostStartLoopTimeEditor from '@/Components/PostStartLoopTimeEditor';
import ProductList from '../productList';
import TagLabel from '../tagLabel';
import UserRoleLabel from '../userRoleLabel';
import InfoImageModal from './InfoImageModal';
import VodThumbnailModal from './VodThumbnailModal';
import dayjs, { Dayjs } from 'dayjs';

const vodTagOptions: {
  value: VodTag;
  label: string;
}[] = [
  { value: 'FLAGSHIP_STORE', label: '旗艦店' },
  { value: 'REBATE', label: '回贈' },
  { value: 'SPONSOR', label: '贊助' },
  { value: 'REPLAY', label: '重播' },
  { value: 'LIMITED_TIME', label: '限時' },
];

const PostDetailCard = () => {
  const dispatch = useAppDispatch();
  const postDetailData = useAppSelector((state) => state.modal.value.postDetail);
  const loading = useAppSelector((state) => state.modal.loading);
  const mainRef = useRef<HTMLParagraphElement | null>(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isAnnouncementModalVisible, setAnnouncementModalVisible] = useState(false);
  const [isUpdateThumbnailModalVisible, setUpdateThumbnailModalVisible] = useState(false);
  const [announcement, setAnnouncement] = useState(postDetailData?.announcement || '');
  const [isUploadModalVisible, setUploadModalVisible] = useState(false);
  const [disabledDate, setDisabledDate] = useState<Dayjs | null>(null);
  const [startLoopTime, setStartLoopTime] = useState<{
    timestamp: number | null;
    timeObject: TimeObject | null;
  }>({
    timestamp: null,
    timeObject: null,
  });

  useEffect(() => {
    setDisabledDate(postDetailData?.disabledDate ? dayjs(postDetailData?.disabledDate) : null);
    const ts = postDetailData?.startLoopTimestamp;
    if (ts === undefined || ts === null) {
      setStartLoopTime({
        timestamp: null,
        timeObject: null,
      });
    } else {
      setStartLoopTime({
        timestamp: ts,
        timeObject: getTimeObjectFromMiliSecond(ts),
      });
    }
  }, [postDetailData]);

  useEffect(() => {
    if (!loading) {
      mainRef.current?.scrollTo(0, 0);
    }
  }, [loading]);

  const shouldShowVideo = useMemo(() => {
    if (!postDetailData) {
      return false;
    }

    const { vod } = postDetailData;
    return Boolean(vod);
  }, [postDetailData]);

  const openEditSkuModal = () => {
    setModalVisible(true);
  };

  const skuList: PostSku[] = useMemo(() => {
    if (postDetailData?.skus && postDetailData?.skus?.length > 0) {
      return postDetailData.skus.map((sku) => ({
        id: sku.skuCode,
        imageUrl: sku.skuImageUrl,
        timestamp: sku.timestamp,
        isNewlyAdded: false,
        dndId: '',
      }));
    }

    if (postDetailData?.littleMallSkus && postDetailData?.littleMallSkus?.length > 0) {
      return postDetailData.littleMallSkus.map((sku) => ({
        id: sku.storeSkuId,
        imageUrl: sku.mainPhoto,
        timestamp: sku.timestamp,
        isNewlyAdded: false,
        dndId: '',
      }));
    }

    if (postDetailData?.qcsSkus && postDetailData?.qcsSkus?.length > 0) {
      return postDetailData.qcsSkus.map((sku) => ({
        id: sku.code,
        imageUrl: sku.imageUrl,
        timestamp: postDetailData.skuTimestamp.qcsSkus.find((t) => t.skuCode === sku.code)
          ?.timestamp,
        isNewlyAdded: false,
        dndId: '',
      }));
    }

    return [];
  }, [
    postDetailData?.skus,
    postDetailData?.littleMallSkus,
    postDetailData?.qcsSkus,
    postDetailData?.skuTimestamp?.qcsSkus,
  ]);

  const skuType = useMemo(() => {
    if (postDetailData?.skus?.length > 0) {
      return SkuType.HKTVmall;
    }

    if (postDetailData?.littleMallSkus?.length > 0) {
      return SkuType.LittleMall;
    }

    if (postDetailData?.qcsSkus?.length > 0) {
      return SkuType.QCS;
    }

    return null;
  }, [postDetailData?.littleMallSkus, postDetailData?.qcsSkus, postDetailData?.skus]);

  const timestampList: PinnedSku[] = useMemo(() => {
    if (postDetailData?.skuTimestamp?.skus?.length > 0) {
      return postDetailData.skuTimestamp.skus.map((sku) => ({
        id: sku.skuCode,
        timestamp: sku.timestamp,
      }));
    }

    if (postDetailData?.skuTimestamp?.littleMallSkus?.length > 0) {
      return postDetailData.skuTimestamp.littleMallSkus.map((sku) => ({
        id: sku.skuCode,
        timestamp: sku.timestamp,
      }));
    }

    if (postDetailData?.skuTimestamp?.qcsSkus?.length > 0) {
      return postDetailData.skuTimestamp.qcsSkus.map((sku) => ({
        id: sku.skuCode,
        timestamp: sku.timestamp,
      }));
    }

    return [];
  }, [
    postDetailData?.skuTimestamp?.littleMallSkus,
    postDetailData?.skuTimestamp?.skus,
    postDetailData?.skuTimestamp?.qcsSkus,
  ]);

  const onEditChangeThumbnailClick = () => {
    setUpdateThumbnailModalVisible(true);
  };

  const onEditAnnouncementClick = () => {
    setAnnouncement(postDetailData.announcement || '');
    setAnnouncementModalVisible(true);
  };

  const onConfirmAnnouncementClick = () => {
    dispatch(updatePostAnnouncement({ postUuid: postDetailData.uuid, announcement }));
    setAnnouncementModalVisible(false);
  };

  const onCancel = () => {
    setAnnouncement(postDetailData.announcement || '');
    setAnnouncementModalVisible(false);
  };

  const onVodTagChange = (tag?: VodTag) => {
    dispatch(updateVodPostTag({ uuid: postDetailData.uuid, tag }));
  };

  const formatSelectedDateTime = (value: Dayjs) =>
    value
      .minute(value.minute() >= 30 ? 30 : 0)
      .second(0)
      .format('YYYY-MM-DD HH:mm:ss');

  const updatePostDisabledDate = () => {
    dispatch(
      patchPostData([
        {
          postUuid: postDetailData.uuid,
          disabledDate,
        },
      ]),
    );
  };

  const onTimeObjectChange = (timeObject: TimeObject) => {
    setStartLoopTime({
      timestamp: convertTimeObjectToMiliSecond(timeObject),
      timeObject,
    });
  };

  const updatePostStartLoopTime = () => {
    try {
      if (startLoopTime.timestamp !== null && startLoopTime.timestamp < 0) {
        throw new TypeError('Invalid data in timestamp');
      }
      dispatch(
        patchPostData([
          {
            postUuid: postDetailData.uuid,
            startLoopTimestamp: startLoopTime.timestamp,
          },
        ]),
      );
    } catch (e) {
      message.error({
        content: `${e}, please check if you have type the right value`,
        duration: 3,
        style: {
          marginTop: '120px',
        },
      });
    }
  };

  return !loading && postDetailData ? (
    <div className="postDetailCard--root" ref={mainRef}>
      {/* postDetailCard__header */}
      <div className="postDetailCard__header">
        <div className="postDetailCard__header__user">
          <div className="flex items-center">
            <Avatar
              shape="square"
              size={32}
              src={postDetailData.user?.profileIconUrl}
              icon={<UserOutlined />} // 預設頭貼
            />
            <div className="postDetailCard__header__user__name">{postDetailData?.user?.name}</div>
            {postDetailData.user?.kocUserRole && postDetailData.user?.kocUserRole !== 'CUSTOMER' ? (
              <UserRoleLabel userRoleStr={postDetailData.user?.kocUserRole as UserRoleEnums} />
            ) : (
              <TagLabel
                labelType="level"
                levelType={
                  postDetailData.user?.membershipLevel.toLowerCase() as 'normal' | 'vip' | 'goldvip'
                }
              />
            )}
          </div>
          <div className="flex items-center">
            {postDetailData.attachmentType === 'LONG_VIDEOS' && (
              <Select
                defaultValue={postDetailData?.vodTag}
                options={vodTagOptions}
                onChange={onVodTagChange}
                style={{ width: 120, marginRight: 10 }}
                allowClear
              />
            )}
            {postDetailData.user.store ? (
              <>
                <ShopOutlined />
                <Tag>{postDetailData.user.store?.platform}</Tag>
                <div className="font-bold">&nbsp;Store Code&nbsp;</div>
                <Tag>{postDetailData.user.store?.code}</Tag>
              </>
            ) : null}
          </div>
        </div>
        <div className="postDetailCard__header__postData">
          <div className="postDetailCard__header__postData__statistic">
            <Statistic
              className="statistic"
              title="Like"
              value={postDetailData.statistics?.likeCount}
              prefix={<HeartOutlined />}
            />
            <Statistic
              className="statistic"
              title="Comment"
              value={postDetailData.statistics?.commentCount}
              prefix={<MessageOutlined />}
            />
            <Statistic
              className="statistic"
              title="Reported"
              value={postDetailData.statistics?.reportCount}
              prefix={<WarningOutlined />}
            />
          </div>
          <div className="postDetailCard__header__postData__other">
            <p>Street: {postDetailData?.categories[0].categoryName || '-'}</p>
            <p>Attachment Type: {postDetailData?.attachmentType}</p>
            <p>Last Updates Time: {timeTranslate(postDetailData?.lastModifiedDate, true)}</p>
            <p>Post Created Time: {timeTranslate(postDetailData?.creationDate, true)}</p>
          </div>
        </div>
      </div>
      {/* postDetailCard__content */}
      <div className="postDetailCard__content">
        <div className="postDetailCard__content__left">
          {shouldShowVideo ? (
            <div className="postDetail__video">
              <PostVideoPlayer
                src={postDetailData.vod?.videoSmilUrl}
                poster={postDetailData.vod?.thumbnailId}
              />
            </div>
          ) : (
            <div className="postDetail__image">
              <Carousel className="postDetail__image__bg">
                {postDetailData?.medias?.map((e) => (
                  <img src={e.url} alt={e.imageId} key={e.imageId} />
                ))}
              </Carousel>
            </div>
          )}
          <div className="postDetail__disabledDate">
            <div className="flex flex-row items-center gap-4">
              <div className="postDetail__disabledDate__title">Disabled Date</div>
              {postDetailData?.attachmentType === 'LONG_VIDEOS' && (
                <div className="flex flex-row items-center justify-between flex-1">
                  <DatePicker
                    showTime={{
                      minuteStep: 30,
                      showSecond: false,
                      defaultValue: dayjs()
                        .startOf('hour')
                        .minute(dayjs().minute() >= 30 ? 30 : 0),
                    }}
                    disabledDate={(current) => current.startOf('day') <= dayjs()}
                    showNow={false}
                    onChange={(value) => setDisabledDate(value)}
                    format={formatSelectedDateTime}
                    value={disabledDate}
                  />
                  <Button onClick={updatePostDisabledDate}>Save</Button>
                </div>
              )}
            </div>
          </div>

          <div className="postDetail__startLoopTimestamp">
            <div className="flex flex-row items-center gap-4">
              <div className="postDetail__startLoopTimestamp__title">Start Loop Time</div>
              {postDetailData?.attachmentType === 'LONG_VIDEOS' && (
                <div className="flex flex-row items-center justify-between flex-1">
                  <PostStartLoopTimeEditor
                    timeObject={startLoopTime.timeObject || { hour: '', minute: '', second: '' }}
                    onTimeObjectChange={onTimeObjectChange}
                  />
                  <Button
                    danger
                    onClick={() =>
                      setStartLoopTime({
                        timestamp: null,
                        timeObject: { hour: '', minute: '', second: '' },
                      })
                    }
                  >
                    Clear
                  </Button>
                  <Button onClick={updatePostStartLoopTime}>Save</Button>
                </div>
              )}
            </div>
          </div>

          {postDetailData?.attachmentType === 'LONG_VIDEOS' && (
            <div className="postDetail__change_thumbnail flex flex-row justify-between items-center">
              <div className="postDetail__change_thumbnail__title">Change Thumbnail</div>
              <Button
                className="postDetail__change_thumbnail__editButton"
                onClick={onEditChangeThumbnailClick}
              >
                Change
              </Button>
            </div>
          )}
          <div className="postDetail__announcement">
            <div className="postDetail__announcement__topBar flex flex-row justify-between items-center">
              <div className="postDetail__announcement__title">Announcement</div>
              {postDetailData?.attachmentType === 'LONG_VIDEOS' && (
                <Button
                  className="postDetail__announcement_editButton"
                  onClick={onEditAnnouncementClick}
                >
                  Edit
                </Button>
              )}
            </div>
          </div>
          {postDetailData?.attachmentType === 'LONG_VIDEOS' && (
            <div className="postDetail__info_image flex flex-col gap-2.5">
              <div className="flex flex-row justify-between items-center">
                <div className="postDetail__info_image__title">Additional Information:</div>
                <Button onClick={() => setUploadModalVisible(true)}>Edit</Button>
              </div>
            </div>
          )}
          <div className="postDetail__productList">
            <div className="postDetail__productList__topBar flex flex-row justify-between">
              <div className="postDetail__productList__title">Products</div>
              <Button
                className="postDetail__productList__addSkuButton"
                onClick={openEditSkuModal}
              >
                Edit
              </Button>
            </div>
            {postDetailData?.skus?.length > 0 && (
              <ProductList productArray={postDetailData?.skus} />
            )}
            {postDetailData?.littleMallSkus?.length > 0 && (
              <ProductList littleMallProductArray={postDetailData?.littleMallSkus} />
            )}
            {postDetailData?.qcsSkus?.length > 0 && (
              <ProductList qcsProducts={postDetailData?.qcsSkus} />
            )}
          </div>
        </div>
        <div className="postDetailCard__content__right">
          <div className="postDetail__postTitle">{postDetailData?.title}</div>
          <div className="postDetail__postContent">{postDetailData?.content}</div>
          <div className="postDetail__postTag">
            {postDetailData?.tags?.length > 0 &&
              postDetailData?.tags?.map((e, index) => <Tag key={index?.toString()}>{e}</Tag>)}
          </div>
        </div>
      </div>
      <EditSkuModal
        isModalVisible={isModalVisible}
        setModalVisible={setModalVisible}
        postUuid={postDetailData.uuid}
        postOwnerRole={postDetailData.user.kocUserRole}
        skuList={skuList}
        skuType={skuType}
        timestampList={timestampList}
        supportEditSkuTimestamp={postDetailData?.attachmentType === 'LONG_VIDEOS'}
      />
      {postDetailData?.attachmentType === 'LONG_VIDEOS' && (
        <VodThumbnailModal
          postUuid={postDetailData.uuid}
          visible={isUpdateThumbnailModalVisible}
          setVisible={setUpdateThumbnailModalVisible}
        />
      )}
      {postDetailData?.attachmentType === 'LONG_VIDEOS' && (
        <Modal
          open={isAnnouncementModalVisible}
          centered
          okText="Confirm"
          onOk={onConfirmAnnouncementClick}
          onCancel={onCancel}
        >
          <div>
            <span>Announcement:</span>
            <Input.TextArea
              className="mb-5"
              defaultValue={postDetailData.announcement}
              allowClear
              showCount
              maxLength={300}
              value={announcement}
              autoSize={{ minRows: 3 }}
              onChange={(e) => {
                setAnnouncement(e.target.value);
              }}
            />
          </div>
        </Modal>
      )}
      {postDetailData?.attachmentType === 'LONG_VIDEOS' && (
        <InfoImageModal
          postUuid={postDetailData.uuid}
          visible={isUploadModalVisible}
          setVisible={setUploadModalVisible}
        />
      )}
    </div>
  ) : (
    <div className="postDetailCard--loading">
      <Spin size="large" />
    </div>
  );
};
export default PostDetailCard;
